import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type ColumnSizingState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { InView } from 'react-intersection-observer';
import { cn } from '../../lib/utils';
import { Pagination, type PaginationProps } from '../Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { ExpandableTruncatedText } from './ExpandableTruncatedText';
export type { ColumnDef } from '@tanstack/react-table';

export interface DataTableProps<TData>
  extends
    Pick<
      TableOptions<TData>,
      | 'columns'
      | 'data'
      | 'pageCount'
      | 'manualPagination'
      | 'debugTable'
      | 'onPaginationChange'
      | 'onRowSelectionChange'
      | 'getRowId'
      | 'autoResetPageIndex'
      | 'enableColumnResizing'
      | 'enableSortingRemoval'
    >,
    Pick<PaginationProps, 'pageLabel' | 'itemsLabel' | 'ofLabel'> {
  className?: string;
  'data-testid'?: string;
  pageSize?: number;
  /** Sort applied on mount. Sorting stays uncontrolled after that. */
  initialSorting?: SortingState;
  onRowClick?: (row: TData) => void | Promise<void>;
  /** Extra classes per row, by row data — e.g. to dim/flag soft-deleted rows. */
  rowClassName?: (row: TData) => string | undefined;
  pagination?: PaginationState | false;
  onScrollEnd?: () => void;
  rowSelection?: RowSelectionState;
  enableRowSelection?: TableOptions<TData>['enableRowSelection'];
  /**
   * Shows the full plain-text cell value in a tooltip.
   * Skips columns that define a custom `cell` renderer (badges, actions, checkboxes).
   * @default false — opt in so existing onRowClick tables keep working
   */
  enableExtend?: boolean;
  columnVisibility?: VisibilityState;
  /**
   * Total row count behind the pagination range label. Required with
   * `manualPagination`, where `data` holds only the current page; client-side
   * tables derive it from the filtered rows.
   */
  totalItems?: number;
}

export function DataTable<TData>({
  columns,
  data,
  className,
  'data-testid': dataTestId,
  pageSize,
  initialSorting = [],
  onRowClick,
  rowClassName,
  pagination,
  onScrollEnd,
  rowSelection = {},
  columnVisibility = {},
  enableColumnResizing = false,
  enableExtend = false,
  manualPagination,
  totalItems,
  pageLabel,
  itemsLabel,
  ofLabel,
  ...props
}: DataTableProps<TData>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const tableRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    defaultColumn: {
      // reset tan stack table default column size to undefined
      size: undefined,
      minSize: 20,
    },
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      columnSizing,
      ...(pagination && { pagination }),
    },
    enableRowSelection: true,
    manualPagination,
    columnResizeMode: 'onChange',
    enableColumnResizing,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pagination === false ? undefined : getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    ...props,
  });

  useEffect(() => {
    if (pageSize !== undefined && pageSize !== table.getState().pagination.pageSize) {
      table.setPageSize(pageSize);
    }
  }, [pageSize, table]);

  const customCellColumnIds = useMemo(() => {
    const ids = new Set<string>();
    const visit = (defs: typeof columns) => {
      defs.forEach((def) => {
        if ('columns' in def && def.columns) {
          visit(def.columns);
          return;
        }
        if (!('cell' in def) || def.cell == null) return;
        // Same fallbacks as TanStack table-core: id, accessorKey ('.' -> '_'), string header.
        const id =
          'id' in def && def.id
            ? def.id
            : 'accessorKey' in def && def.accessorKey != null
              ? String(def.accessorKey).replaceAll('.', '_')
              : typeof def.header === 'string'
                ? def.header
                : '';
        if (id) ids.add(id);
      });
    };
    visit(columns);
    return ids;
  }, [columns]);

  // Under `manualPagination` the row model only holds the current page, so the
  // count has to come from the caller; without it the label falls back to pages.
  const resolvedTotalItems = manualPagination
    ? totalItems
    : (totalItems ?? table.getFilteredRowModel().rows.length);

  const visibleColumns = table.getVisibleLeafColumns();
  const colTemplate = useMemo(() => {
    return visibleColumns
      .map((col) => {
        if (col.id in columnSizing) return `${col.getSize()}px`;
        const { size, minSize, maxSize } = col.columnDef;
        if (size != null) {
          return minSize != null ? `minmax(${minSize}px,${size}px)` : `${size}px`;
        }
        if (minSize != null && maxSize != null) return `minmax(${minSize}px,${maxSize}px)`;
        if (minSize != null) return `minmax(${minSize}px,1fr)`;
        if (maxSize != null) return `minmax(0px,${maxSize}px)`;
        return '1fr';
      })
      .join(' ');
  }, [visibleColumns, columnSizing]);

  return (
    <TooltipProvider>
      <div className="flex min-h-0 flex-1 flex-col gap-3" ref={tableRef} data-testid={dataTestId}>
        <div className={cn('flex min-h-0 flex-1 rounded-lg border', className)}>
          <Table columns={colTemplate}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className="relative overflow-visible"
                    >
                      {!header.isPlaceholder && (
                        <div
                          className={cn(
                            'flex min-w-0 flex-1 items-center gap-2 overflow-hidden',
                            header.column.getCanSort() && 'cursor-pointer select-none',
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <TruncatedHeaderLabel>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </TruncatedHeaderLabel>
                          {header.column.getCanSort() && (
                            <span className="shrink-0 text-muted-foreground">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ArrowUp className="h-4 w-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ArrowDown className="h-4 w-4" />
                              ) : null}
                            </span>
                          )}
                        </div>
                      )}
                      {!header.isPlaceholder && header.column.getCanResize() && (
                        <ColumnResizeHandle header={header} />
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={(e) => {
                      if (tableRef.current && tableRef.current.contains(e.target as Node)) {
                        onRowClick?.(row.original);
                      }
                    }}
                    className={cn(
                      row.getIsSelected() && 'bg-muted/50',
                      onRowClick && 'cursor-pointer',
                      rowClassName?.(row.original),
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {(() => {
                          const rendered = flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          );
                          const value = cell.getValue();
                          const isPlainText =
                            typeof value === 'string' || typeof value === 'number';
                          if (
                            enableExtend &&
                            isPlainText &&
                            String(value) &&
                            !customCellColumnIds.has(cell.column.id)
                          ) {
                            return <ExpandableTruncatedText text={String(value)} />;
                          }
                          return <div className="min-w-0 flex-1 truncate">{rendered}</div>;
                        })()}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
              {onScrollEnd && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-4 p-0">
                    <InView onChange={(inView) => inView && onScrollEnd()}>
                      <div className="h-4" />
                    </InView>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {pagination !== false && (
          <Pagination
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            pageSize={table.getState().pagination.pageSize}
            totalItems={resolvedTotalItems}
            pageLabel={pageLabel}
            itemsLabel={itemsLabel}
            ofLabel={ofLabel}
            onPageChange={(page) => table.setPageIndex(page - 1)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

/** Shows the full header text when the column clips it. */
function TruncatedHeaderLabel({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  const label =
    typeof children === 'string' || typeof children === 'number' ? String(children) : undefined;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || label === undefined) return;
    const update = () => setTruncated(element.scrollWidth > element.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [label, truncated]);

  const text = (
    <span ref={ref} className="min-w-0 flex-1 truncate">
      {children}
    </span>
  );

  if (!truncated || label === undefined) return text;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{text}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
